import React, { useEffect, useRef, useState } from "react";
import { Zap, Image as ImageIcon, Film, Bot } from "lucide-react";

const stats = [
  { icon: Zap,       value: 5,    suffix: "×",  label: "schneller", desc: "Workflow-Beschleunigung mit Freycreate.ai vs. klassische Produktion" },
  { icon: ImageIcon, value: 1500, suffix: "+",  label: "KI-Bilder",  desc: "generiert in den letzten 12 Monaten" },
  { icon: Film,      value: 200,  suffix: "+",  label: "KI-Videos",  desc: "von Marketing-Spots bis B-Roll-Material" },
  { icon: Bot,       value: 5,    suffix: "",   label: "KI-Agenten", desc: "spezialisiert auf Creative · Coding · Research · Planning · Testing" },
];

// Animated counter hook
const useCounter = (target, durationMs = 1600, ref) => {
  const [val, setVal] = useState(0);
  const startedRef = useRef(false);
  useEffect(() => {
    const node = ref?.current;
    if (!node || !("IntersectionObserver" in window)) {
      setVal(target);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min(1, (now - start) / durationMs);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(target * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [target, durationMs, ref]);
  return val;
};

const StatCard = ({ stat }) => {
  const ref = useRef(null);
  const v = useCounter(stat.value, 1600, ref);
  const Icon = stat.icon;
  return (
    <div
      ref={ref}
      className="relative bg-background/40 border border-white/5 rounded-2xl p-6 hover:border-[hsl(var(--accent))]/40 transition-colors"
    >
      <div className="absolute -top-3 -left-3 w-10 h-10 rounded-xl bg-[hsl(var(--accent))]/20 border border-[hsl(var(--accent))]/40 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[hsl(var(--accent))]" />
      </div>
      <div className="text-4xl sm:text-5xl font-bold mb-1 neon-text text-[hsl(var(--accent))] tabular-nums">
        {v.toLocaleString("de-DE")}
        <span className="text-2xl ml-0.5">{stat.suffix}</span>
      </div>
      <div className="text-sm font-semibold mb-1">{stat.label}</div>
      <div className="text-xs text-muted-foreground leading-snug">{stat.desc}</div>
    </div>
  );
};

const AiStats = () => (
  <section className="py-16 sm:py-20 bg-background" data-testid="ai-stats">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <StatCard key={i} stat={s} />
        ))}
      </div>
    </div>
  </section>
);

export default AiStats;
